import { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Autocomplete from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import TerrainIcon from '@mui/icons-material/Terrain';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { stoneSort } from '../docFormat';
import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { useBranch } from '../../../contextApi/BranchContext';
import { usePermissions } from '../../../contextApi/PermissionContext';
import { saveMisPackingList } from '../../../store/store';
import StoneNamesField from './stoneNamesField';
import PlaceSelect, { usePackingPlaces } from './placeSelect';
import { usePalletTemplates, SavePalletTemplateDialog, PalletTemplateMenu } from './palletTemplates';
import {
  EMPTY_ITEM, EMPTY_PALLET, hasValue, withKey, fixed4, productPrefix, palletClashes, rowHasData, palletForSave, palletFromRecord,
  Section, PalletCard, PHONE_BTN, PHONE_FORM_SX, usePalletEditor, usePalletCodes,
} from './palletEditor';
import PalletCodeAdder from './palletCodeAdder';
import LabelFormPickerButton from './labelFormPicker';
import MisDateField from '../misDateField';
import { DateSystemSelect, branchDocDefaults } from '../docSettings';
import { DraftCloseDialog, newDraftKey, MIS_DRAFT } from '../misDraftStrip';
import { normalizeDateSystem, toIsoDay, today } from '../../../tools/calendars';
import useFormDraft from '../../../tools/hooks/useFormDraft';

// What the form holds of the driver and of the two handlers. The driver's national ID and smart number are ONE field now
// ("123 / 456"), so a list (or an unsent draft) that still has them apart shows them together; the IBAN, and the phone
// numbers of the customs agent and the loading officer, are not asked for any more. They stay in a list saved before,
// until that list is saved again.
const driverFromStored = (d) => {
  const x = d || {};
  return {
    fullName: x.fullName || '',
    nationalId: [x.nationalId, x.smartNumber].map((s) => String(s == null ? '' : s).trim()).filter(Boolean).join(' / '),
    phone: x.phone || '',
  };
};
const nameOnly = (o) => ({ name: (o && o.name) || '' });

// preset (new lists only, optional) — raised from a Supply record:
//   supplyRecordId — the list is linked to that record (shows under it)
//   recordTitle    — shown in the banner, so it's clear where it will land
//   productCode    — seeds the first pallet with the record's product
// draft (new lists only, optional) — a stored unsent list (components/mis/misDraftStrip.js) to
// carry on with. A new list is kept in this browser while it is being filled
// (tools/hooks/useFormDraft.js); nothing reaches the server until Save.
export default function PackingListForm({ open, onClose, packingList = null, preset = null, draft = null }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const { activeBranchId, activeBranch } = useBranch();
  const { can } = usePermissions();
  const canCreateVariety = can('inventory:subproduct:create');

  const isEdit = Boolean(packingList);
  // The supply record a list was raised from survives in its draft too.
  const effPreset = (!isEdit && draft && draft.snapshot && draft.snapshot.preset) || preset;
  // The places the loading origin and the final destination are picked from (kept per branch, added to from this form).
  const canManagePlaces = can('mis:packingList:create') || can('mis:packingList:edit');
  const { places, add: addPlace, remove: removePlace } = usePackingPlaces(isEdit ? packingList.branchId : activeBranchId, open);
  // Pallets kept under a name, to start another pallet from (same people, same branch rule as the places).
  const { templates, save: saveTemplate, remove: removeTemplate } = usePalletTemplates(isEdit ? packingList.branchId : activeBranchId, open);
  const [templateFor, setTemplateFor] = useState(null);   // _key of the pallet being saved as a template

  const T = useMemo(() => ({
    BD:       isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider,
    BD2:      isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    TEXT_PRI: isDark ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary,
    TEXT_SEC: isDark ? 'rgba(255,255,255,0.45)' : theme.palette.text.secondary,
    TEXT_TER: isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.3)',
    CTRL_BG:  isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    ROW_ALT:  isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',   // every second row of a pallet: a band the eye can follow
    PULSE:    isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.14)',
  }), [isDark, theme.palette.divider, theme.palette.text.primary, theme.palette.text.secondary]);

  const [type, setType] = useState('free');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);   // full docs (need lineItems for scope)
  const [driverInfo, setDriverInfo] = useState({ fullName: '', nationalId: '', phone: '' });
  const [vehicleInfo, setVehicleInfo] = useState({ trailerPlateNumber: '', trailerSmartNumber: '' });
  const [customsAgent, setCustomsAgent] = useState({ name: '' });
  const [loadingOfficer, setLoadingOfficer] = useState({ name: '' });
  const [originAddress, setOriginAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [shippingDestination, setShippingDestination] = useState('');
  const [exitNumber, setExitNumber] = useState('');
  const [standardThicknessCm, setStandardThicknessCm] = useState('');
  const [stoneNames, setStoneNames] = useState([]);     // [{ name } | { productId, code, name, nameAr, nameFa }]
  const editor = usePalletEditor({ thickness: standardThicknessCm, branchId: activeBranchId });
  const {
    pallets, setPallets, palletsRef, specKey, setSpecKey, movedKey, announce, flip, addPallet, addFromTemplate, addExisting, onInsertKey, duplicatePallet,
    removePallet, movePalletBy, movePalletTo, setPalletField, addItem, enterRow, removeItem, setItemField, resetSqm, toggleSpec, addBuiltItem, searchForBuilder,
  } = editor;
  const [status, setStatus] = useState('draft');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The day the list is dated, and the calendar it is entered and printed in.
  const [issueDate, setIssueDate] = useState(() => toIsoDay(today()));
  const [dateSystem, setDateSystem] = useState('gregorian');
  // The unsent draft of a NEW list: every opening has its own key, a continued one re-uses its key.
  const [draftKey, setDraftKey] = useState('');
  const [fromDraft, setFromDraft] = useState(false);
  const [restoringIds, setRestoringIds] = useState([]);   // linked invoices of a restored draft, still being fetched
  const [hydratedAt, setHydratedAt] = useState(0);
  const [baseline, setBaseline] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  // The packing list NUMBER: automatic (the branch's next one, which the server suggests) until the person
  // types their own; on an existing list it is the current number and can be changed.
  const [docNumber, setDocNumber] = useState('');
  const [numberTouched, setNumberTouched] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [numberError, setNumberError] = useState('');
  const numberRef = useRef(null);

  // ── hydrate ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setError(''); setSpecKey(null); setConfirmClose(false);
    setFromDraft(false); setRestoringIds([]); setNumberError('');
    if (packingList) {
      setDocNumber(String(packingList.docNumber)); setNumberTouched(false);
      // a list made before lists were dated has no date of its own: the day it was created
      setIssueDate(toIsoDay(packingList.issueDate || packingList.insertDate) || toIsoDay(today()));
      setDateSystem(normalizeDateSystem(packingList.dateSystem));
      setType(packingList.type);
      setDriverInfo(driverFromStored(packingList.driverInfo));
      setVehicleInfo({ trailerPlateNumber: '', trailerSmartNumber: '', ...packingList.vehicleInfo });
      setCustomsAgent(nameOnly(packingList.customsAgent));
      setLoadingOfficer(nameOnly(packingList.loadingOfficer));
      setOriginAddress(packingList.originAddress || '');
      setDestinationAddress(packingList.destinationAddress || '');
      setShippingDestination(packingList.shippingDestination || '');
      setExitNumber(packingList.exitNumber || '');
      setStandardThicknessCm(packingList.standardThicknessCm ?? '');
      setStoneNames(Array.isArray(packingList.stoneNames) ? packingList.stoneNames.map((s) => ({ ...s })) : []);
      setPallets(packingList.pallets?.length
        ? packingList.pallets.map((p) => withKey({
            ...p,
            items: p.items?.length
              ? p.items.map((it) => ({ ...it, sort: stoneSort(it.sort), sqm: fixed4(it.sqm), sqmTouched: true }))   // an area stays as stored until L, W or pcs changes, then it is calculated again
              : [{ ...EMPTY_ITEM }],
          }))
        : [withKey({ ...EMPTY_PALLET })]);
      setStatus(packingList.status || 'draft');
      setNotes(packingList.notes || '');
      setSelectedInvoices([]);   // resolved by the effect below (needs real docs, not bare ids)
    } else if (draft && draft.snapshot) {
      // Carrying on an unsent list: the form opens exactly as it was left.
      const d = draft.snapshot;
      setDraftKey(draft.key);
      setFromDraft(true);
      // a number the person typed is kept; an untouched one is the suggestion, fetched afresh
      setDocNumber(d.docNumberTouched && d.docNumber ? String(d.docNumber) : '');
      setNumberTouched(Boolean(d.docNumberTouched && d.docNumber));
      setIssueDate(d.issueDate || toIsoDay(today()));
      setDateSystem(normalizeDateSystem(d.dateSystem));
      setType(d.type === 'linked' ? 'linked' : 'free');
      setSelectedInvoices([]);
      setRestoringIds(Array.isArray(d.invoiceIds) ? d.invoiceIds : []);
      setDriverInfo(driverFromStored(d.driverInfo));
      setVehicleInfo({ trailerPlateNumber: '', trailerSmartNumber: '', ...d.vehicleInfo });
      setCustomsAgent(nameOnly(d.customsAgent));
      setLoadingOfficer(nameOnly(d.loadingOfficer));
      setOriginAddress(d.originAddress || ''); setDestinationAddress(d.destinationAddress || '');
      setShippingDestination(d.shippingDestination || '');
      setExitNumber(d.exitNumber || '');
      setStandardThicknessCm(d.standardThicknessCm == null ? '' : d.standardThicknessCm);
      setStoneNames(Array.isArray(d.stoneNames) ? d.stoneNames : []);
      setPallets(Array.isArray(d.pallets) && d.pallets.length
        ? d.pallets.map((p) => withKey({ ...p, items: p.items && p.items.length ? p.items.map((it) => ({ ...it, sort: stoneSort(it.sort) })) : [{ ...EMPTY_ITEM }] }))
        : [withKey({ ...EMPTY_PALLET })]);
      setStatus(d.status === 'final' ? 'final' : 'draft'); setNotes(d.notes || '');
    } else {
      setDraftKey(newDraftKey('packingList', activeBranchId));
      setDocNumber(''); setNumberTouched(false);
      setIssueDate(toIsoDay(today()));
      setDateSystem(branchDocDefaults(activeBranch).dateSystem);
      setType('free'); setSelectedInvoices([]);
      setDriverInfo({ fullName: '', nationalId: '', phone: '' });
      setVehicleInfo({ trailerPlateNumber: '', trailerSmartNumber: '' });
      setCustomsAgent({ name: '' });
      setLoadingOfficer({ name: '' });
      // a list made from a label form starts at that form's origin and destination
      setOriginAddress(preset?.originAddress || ''); setDestinationAddress(preset?.destinationAddress || ''); setShippingDestination('');
      setExitNumber('');
      setStandardThicknessCm('');
      setStoneNames([]);
      setPallets(preset?.pallets?.length
        // pallets ticked on the Labels screen: each keeps its P number if it has one, the rest are numbered in order
        ? preset.pallets.map((rec, i) => withKey({ ...palletFromRecord(rec), palletId: rec.palletId || `P${i + 1}` }))
        : preset?.productCode
        ? [withKey({ ...EMPTY_PALLET, palletId: 'P1', productCode: preset.productCode,
            items: [{ ...EMPTY_ITEM, code: preset.productCode }] })]
        // the first pallet of a new list starts with an ID, as every pallet added after it does (P2, P3 ...): a row typed into a
        // pallet that has none used to be dropped at Save
        : [withKey({ ...EMPTY_PALLET, palletId: 'P1' })]);
      setStatus('draft'); setNotes('');
    }
    // What "nothing has been changed yet" looks like is read from the render that follows.
    setBaseline(null);
    setHydratedAt(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packingList, draft]);

  // Edit mode (and a restored draft): turn stored invoiceIds into REAL documents, so the chips show
  // "#12 — Customer" instead of a raw ObjectId (and so scope suggestions work).
  useEffect(() => {
    const ids = isEdit ? (packingList?.invoiceIds || []) : ((draft && draft.snapshot && draft.snapshot.invoiceIds) || []);
    if (!open || ids.length === 0) return;
    let cancelled = false;
    Promise.all(ids.map((id) =>
      authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/invoices/${id}` })
        .then((r) => r.data.data || r.data)
        .catch(() => null)
    )).then((docs) => {
      if (!cancelled) { setSelectedInvoices(docs.filter(Boolean)); setRestoringIds([]); }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, packingList?._id, draft]);

  // Invoice search (linked mode only)
  useEffect(() => {
    if (!open || type !== 'linked' || !activeBranchId) { setInvoiceOptions([]); return; }
    const timer = setTimeout(() => {
      authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/invoices`,
        params: { branchId: activeBranchId, docType: 'invoice', limit: 10,
          ...(invoiceSearch.trim() ? { search: invoiceSearch.trim() } : {}) },
      })
        .then((res) => setInvoiceOptions(res.data.data || []))
        .catch(() => setInvoiceOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [open, type, invoiceSearch, activeBranchId, authCtx, axiosGlobal]);

  // ── scope: what codes the linked invoices actually allow ──────────────────
  const scope = useMemo(() => {
    const codes = new Set(), prefixes = new Set();
    for (const inv of selectedInvoices) {
      for (const li of (inv.lineItems || [])) {
        if (!li.code) continue;
        codes.add(li.code);
        prefixes.add(productPrefix(li.code));
      }
    }
    return { codes: [...codes], prefixes };
  }, [selectedInvoices]);

  // Suggestions offered on the item code field: the exact SKUs from the linked
  // invoices first, then their bare product codes (a packing item is often the
  // nominal code, not the full SKU).
  const codeOptions = useMemo(() => {
    if (type !== 'linked') return [];
    return [...scope.codes, ...[...scope.prefixes].filter((p) => !scope.codes.includes(p))];
  }, [type, scope]);

  const outOfScope = useMemo(() => {
    if (type !== 'linked' || scope.prefixes.size === 0) return [];
    const bad = [];
    for (const p of pallets) {
      for (const it of (p.items || [])) {
        if (it.code && it.code.trim() && !scope.prefixes.has(productPrefix(it.code))) bad.push(it.code);
      }
    }
    return [...new Set(bad)];
  }, [type, pallets, scope]);

  // ── totals ────────────────────────────────────────────────────────────────
  const palletTotals = (p) => (p.items || []).reduce((acc, it) => {
    acc.pcs += Number(it.pcs) || 0;
    acc.sqm += Number(it.sqm) || 0;
    return acc;
  }, { pcs: 0, sqm: 0 });

  const totals = pallets.reduce((acc, p) => {
    const pt = palletTotals(p);
    acc.pcs += pt.pcs; acc.sqm += pt.sqm;
    return acc;
  }, { pcs: 0, sqm: 0 });

  const clashes = useMemo(() => palletClashes(pallets), [pallets]);
  const { codeHints, takenFor, takenLine, takenError, setTakenCodes } = usePalletCodes({
    open, pallets, palletsRef, ownPallets: packingList && packingList.pallets, excludeListId: isEdit && packingList ? packingList._id : null, refreshOn: hydratedAt,
  });


  // What the person is told, in words, about the pallets that clash.
  const clashMessage = (groups) => groups.map((g) => (g.field === 'reference'
    ? t('mis.plDuplicateRefError', { pallets: g.pallets.join(', '), value: g.value })
    : t('mis.plDuplicateIdError', { value: g.value }))).join(' ');

  const keepAsTemplate = async (name) => {
    const pallet = palletsRef.current.find((p) => p._key === templateFor);
    if (!pallet) return;
    await saveTemplate({
      name, productCode: pallet.productCode, processingType: pallet.processingType,
      items: pallet.items.filter(rowHasData).map(({ code, sort, lengthCm, widthCm, thicknessCm, pcs, sqm }) => ({ code, sort, lengthCm, widthCm, thicknessCm, pcs, sqm })),
    });
  };
  const askTemplate = useCallback((key) => setTemplateFor(key), []);

  const onFormKeyDown = (event) => onInsertKey(event, saving);

  // Applying a standard thickness fills every blank thickness cell - and updates the cells it filled itself, so typing
  // "1.8" leaves 1.8 everywhere, not the "1" it passed through. A cell the person typed in is never touched.
  const applyStandardThickness = (value) => {
    setStandardThicknessCm(value);
    if (value === '') return;
    setPallets((ps) => ps.map((p) => ({
      ...p,
      items: p.items.map((it) => (!hasValue(it.thicknessCm) || it.thicknessAuto
        ? { ...it, thicknessCm: value, thicknessAuto: true } : it)),
    })));
  };

  // ── the number ───────────────────────────────────────────────────────────────
  // What a new list would get right now. It is only LOOKED at: if the person leaves it alone, the server takes
  // the next free number when the list is saved (so two people filling a form at once never clash).
  useEffect(() => {
    if (!open || isEdit || !activeBranchId) return undefined;
    let cancelled = false;
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/packing-lists/next-number`, params: { branchId: activeBranchId } })
      .then((res) => { if (!cancelled) setSuggested(Number(res.data && res.data.next) || null); })
      .catch(() => { if (!cancelled) setSuggested(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, activeBranchId, hydratedAt]);

  // A number the person typed that another list of the branch already carries is flagged as they type, not
  // after the whole list is filled in. The save checks again: this answer can be a moment out of date.
  useEffect(() => {
    if (!open || !(isEdit || numberTouched)) return undefined;   // an automatic number is free by construction
    const digits = String(docNumber || '');
    if (!digits || (isEdit && packingList && Number(digits) === Number(packingList.docNumber))) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/packing-lists/number-available`,
        params: { branchId: isEdit && packingList ? packingList.branchId : activeBranchId, number: digits, excludeId: isEdit && packingList ? packingList._id : undefined },
      }).then((res) => {
        if (!cancelled && res.data && res.data.valid && res.data.available === false) setNumberError(t('mis.plNumberTaken', { number: Number(digits) }));
      }).catch(() => { /* the save will say it */ });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, numberTouched, docNumber, activeBranchId]);

  const shownNumber = (isEdit || numberTouched) ? docNumber : (suggested == null ? '' : String(suggested));
  const resetNumberToAutomatic = () => { setNumberError(''); setNumberTouched(false); setDocNumber(''); };
  const typeNumber = (raw) => {
    const digits = String(raw).replace(/\D/g, '').slice(0, 8);
    setNumberError('');
    // emptying the box of a NEW list hands the number back to the automatic one
    if (!isEdit && digits === '') { resetNumberToAutomatic(); return; }
    setNumberTouched(true);
    setDocNumber(digits);
  };
  const refuseNumber = (message) => {
    setNumberError(message);
    setTimeout(() => { if (numberRef.current) numberRef.current.focus(); }, 0);
  };

  // ── the unsent draft of a NEW list ───────────────────────────────────────────
  const draftable = !isEdit;
  const signature = JSON.stringify([
    type, selectedInvoices.map((i) => i._id), driverInfo, vehicleInfo, customsAgent, loadingOfficer,
    originAddress, destinationAddress, shippingDestination, exitNumber, standardThicknessCm, stoneNames, pallets, status, notes, issueDate,
    numberTouched ? docNumber : '',     // the suggestion arriving is not a change
  ]);
  // The form as it stood right after it was set up (blank, or seeded from a supply record).
  useEffect(() => {
    if (open && hydratedAt) setBaseline(signature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedAt]);
  // A draft being carried on counts as changed straight away: closing it must ask, not silently drop it.
  const dirty = open && draftable && (fromDraft || (baseline !== null && signature !== baseline));
  const draftTitle = [
    pallets.filter((p) => String(p.palletId || '').trim()).length
      ? t('mis.plPalletCount', { count: pallets.filter((p) => String(p.palletId || '').trim()).length }) : '',
    totals.sqm ? `${totals.sqm.toFixed(4)} ${t('mis.plColSqm')}` : '',
    driverInfo.fullName,
  ].filter(Boolean).join(' · ');
  const draftApi = useFormDraft({
    key: draftKey,
    kind: MIS_DRAFT.packingList.kind,
    enabled: open && draftable && Boolean(draftKey),
    dirty,
    snapshot: {
      type,
      // ids still being fetched stay in the draft: autosaving before they arrive must not lose them
      invoiceIds: restoringIds.length ? restoringIds : selectedInvoices.map((i) => i._id),
      driverInfo, vehicleInfo, customsAgent, loadingOfficer,
      originAddress, destinationAddress, shippingDestination, exitNumber, standardThicknessCm,
      stoneNames, pallets, status, notes, issueDate, dateSystem,
      docNumber: numberTouched ? docNumber : '', docNumberTouched: numberTouched,
      preset: effPreset ? {
        supplyRecordId: effPreset.supplyRecordId, recordTitle: effPreset.recordTitle, productCode: effPreset.productCode,
      } : null,
    },
    meta: { branchId: activeBranchId, title: draftTitle },
  });

  const handleClose = () => {
    if (saving) return;
    if (draftable && dirty) { setConfirmClose(true); return; }
    if (draftable) draftApi.clear();
    onClose();
  };

  const handleSave = async () => {
    if (type === 'linked' && selectedInvoices.length === 0) { setError(t('mis.plSelectInvoiceError')); return; }
    // A pallet is found by its ID, so one that has rows (or a pallet code, or a processing type) but no ID cannot be saved - and it
    // must never be dropped without a word, rows and all. Say which pallet and go to its ID.
    const idless = pallets.findIndex((p) => !String(p.palletId || '').trim()
      && (p.items.some(rowHasData) || String(p.reference || '').trim() || String(p.processingType || '').trim()));
    if (idless >= 0) {
      setError(t('mis.plPalletNeedsId', { no: idless + 1 }));
      const key = pallets[idless]._key;
      setTimeout(() => {
        const el = document.querySelector(`[data-pallet-key="${key}"] input[data-pallet-field="palletId"]`);
        if (el) { el.scrollIntoView({ block: 'center' }); el.focus(); }
      }, 0);
      return;
    }
    if (pallets.every((p) => !String(p.palletId || '').trim())) { setError(t('mis.plAddPalletError')); return; }
    if (outOfScope.length) { setError(t('mis.plOutOfScopeError', { codes: outOfScope.join(', ') })); return; }
    // Two pallets with the same reference (or the same ID) cannot be told apart: it has to be fixed first.
    if (clashes.groups.length) {
      setError(clashMessage(clashes.groups));
      setTimeout(() => { const bad = document.querySelector('[data-pallet-clash="true"] input'); if (bad) bad.focus(); }, 0);
      return;
    }
    // A pallet code another list holds has to be changed first (or emptied, for the next free one).
    const takenHere = pallets.map((p) => takenFor(p.reference)).filter(Boolean);
    if (takenHere.length) {
      setError([...new Set(takenHere.map(takenError))].join(' '));
      setTimeout(() => { const bad = document.querySelector('[data-pallet-clash="true"] input'); if (bad) bad.focus(); }, 0);
      return;
    }
    // A number already flagged as used must be changed, not sent again.
    if (numberError) { refuseNumber(numberError); return; }

    // The number goes to the server only when it is the person's: typed on a new list, changed on an existing
    // one. Otherwise the server takes the next free automatic number at the moment of saving.
    let numberPart = {};
    if (isEdit) {
      if (docNumber.trim() === '') { refuseNumber(t('mis.plNumberRequired')); return; }
      if (Number(docNumber) < 1) { refuseNumber(t('mis.plNumberInvalid')); return; }
      if (Number(docNumber) !== Number(packingList.docNumber)) numberPart = { docNumber: Number(docNumber) };
    } else if (numberTouched) {
      if (Number(docNumber) < 1) { refuseNumber(t('mis.plNumberInvalid')); return; }
      numberPart = { docNumber: Number(docNumber) };
    }

    setSaving(true); setError('');
    try {
      const data = {
        ...numberPart,
        type,
        invoiceIds: type === 'linked' ? selectedInvoices.map((i) => i._id) : [],
        branchId: activeBranchId,
        issueDate, dateSystem,
        driverInfo, vehicleInfo, customsAgent, loadingOfficer,
        originAddress, destinationAddress, shippingDestination, exitNumber,
        standardThicknessCm: standardThicknessCm === '' ? undefined : Number(standardThicknessCm),
        // a picked stone goes as its product (the server re-reads it), a typed one as its name; always sent, so
        // emptying the field on an edit clears them
        stoneNames: stoneNames.map((s) => (s.productId ? { productId: s.productId, name: s.name } : { name: s.name })),
        pallets: pallets.filter((p) => String(p.palletId || '').trim()).map((p) => ({ ...palletForSave(p), palletRecordId: p.palletRecordId })),
        status,
        notes,
        // Raised from a Supply record — link it there (re-validated server-side
        // against this branch).
        ...(!isEdit && effPreset?.supplyRecordId ? { supplyRecordId: effPreset.supplyRecordId } : {}),
      };
      await dispatch(saveMisPackingList({ authCtx, axiosGlobal, id: packingList?._id, data })).unwrap();
      if (draftable) await draftApi.clear();      // it is a real list now
      onClose();
    } catch (err) {
      if (err && err.code === 'DOC_NUMBER_TAKEN') {
        const taken = t('mis.plNumberTaken', { number: err.docNumber != null ? err.docNumber : docNumber });
        refuseNumber(taken);
        setError(taken);
      } else if (err && err.code === 'DUPLICATE_PALLET' && Array.isArray(err.duplicates)) {
        setError(clashMessage(err.duplicates.map((d) => ({ field: d.field, value: d.value, pallets: d.palletIds }))));
      } else if (err && err.code === 'PALLET_CODE_TAKEN' && Array.isArray(err.taken)) {
        // somebody saved that code after the check as you typed: the field shows it, as the check would have
        setTakenCodes((cur) => ({ ...cur, ...Object.fromEntries(err.taken.map((row) => [row.key, row])) }));
        setError(err.taken.map(takenError).join(' '));
      } else if (err && err.code === 'PALLET_ON_OTHER_LIST') {
        setError(t('mis.lblOnOtherList', { code: err.pallet, list: err.docNumber }));
      } else if (err && err.code === 'PALLET_NOT_FOUND') {
        setError(t('mis.lblNotFound', { codes: err.pallet }));
      } else {
        setError((err && err.message) || t('mis.plSaveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  // ── shared field styling ──────────────────────────────────────────────────
  const field = { size: 'small', InputLabelProps: { shrink: true } };

  // Identifiers (IBAN, national ID, smart numbers, plates, phones) are always
  // latin digits read left-to-right, even when the UI is in Arabic or Farsi —
  // forcing dir=ltr stops them rendering reversed in an RTL layout.
  const idField = {
    ...field,
    inputProps: { dir: 'ltr', inputMode: 'numeric' },
    sx: { '& input': { fontFamily: 'monospace', fontSize: '0.8rem', letterSpacing: 0.3 } },
  };
  const telField = {
    ...field,
    type: 'tel',
    inputProps: { dir: 'ltr', inputMode: 'tel' },
    sx: { '& input': { fontFamily: 'monospace', fontSize: '0.8rem' } },
  };

  // The totals chips of the footer are larger on a phone.
  const footerChip = { height: isXs ? 26 : 22, fontSize: isXs ? '0.74rem' : '0.65rem' };

  return (
    <>
    <Drawer anchor="right" open={open} onClose={handleClose}
      PaperProps={{ onKeyDown: onFormKeyDown, sx: { width: isXs ? '100vw' : 760, maxWidth: '100vw', ...(isXs ? PHONE_FORM_SX : {}) } }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: { xs: 2, sm: 3 }, py: 2, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.TEXT_PRI }}>
            {isEdit ? t('mis.plEditTitle') : t('mis.plNewTitle')}
          </Typography>
          {isEdit && (
            <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC }}>#{packingList.docNumber}</Typography>
          )}
        </Box>
        {draftable && draftApi.savedAt && (
          <Typography data-testid="draft-saved" sx={{ fontSize: '0.64rem', color: T.TEXT_TER, ml: 'auto', mr: 1 }}>
            {t('mis.draftSaved', { time: new Date(draftApi.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
          </Typography>
        )}
        <IconButton onClick={handleClose} size="small" aria-label={t('common.close')} sx={isXs ? PHONE_BTN : undefined}>
          <CloseIcon sx={{ fontSize: isXs ? 22 : 18 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, flex: 1, overflowY: 'auto' }}>

        {!isEdit && effPreset?.supplyRecordId && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1.25, py: 1,
            borderRadius: '10px', border: `1px solid ${T.BD}`, bgcolor: T.CTRL_BG }}>
            <TerrainIcon sx={{ fontSize: 16, color: T.TEXT_SEC }} />
            <Typography sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>
              {t('mis.plLinkedToRecord', { record: effPreset.recordTitle || '—' })}
            </Typography>
          </Box>
        )}

        {/* ── Document: its number, its date and the calendar the date is written in ── */}
        <Section T={T} label={t('mis.sectionDocument')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: isXs ? '1fr' : '1fr 1fr', gap: 1.25, alignItems: 'start' }}>
            {/* The number the printed packing list carries. Filled in automatically; type another to change it. */}
            <TextField {...field} label={t('mis.plFieldNumber')} value={shownNumber} inputRef={numberRef}
              onChange={(e) => typeNumber(e.target.value)}
              placeholder={t('mis.plNumberAutomatic')}
              error={Boolean(numberError)}
              helperText={numberError || (isEdit ? t('mis.plNumberEditHelp')
                : numberTouched ? t('mis.plNumberCustomHelp') : t('mis.plNumberAutoHelp'))}
              inputProps={{ inputMode: 'numeric', dir: 'ltr', maxLength: 8, 'data-testid': 'pl-number' }}
              InputProps={{
                endAdornment: !isEdit && numberTouched ? (
                  <InputAdornment position="end">
                    <Tooltip title={t('mis.plNumberUseAuto')}>
                      <IconButton size="small" onClick={resetNumberToAutomatic} aria-label={t('mis.plNumberUseAuto')}>
                        <RestartAltIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ '& input': { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.5 } }} />
            <DateSystemSelect value={dateSystem} onChange={(v) => setDateSystem(v)} />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <MisDateField label={t('mis.plFieldDate')} system={dateSystem} value={issueDate}
                onChange={(iso) => setIssueDate(iso)} />
            </Box>
            <TextField {...field} label={t('mis.plFieldExitNumber')} value={exitNumber}
              inputProps={{ dir: 'ltr', maxLength: 60, 'data-testid': 'pl-exit-number' }}
              onChange={(e) => setExitNumber(e.target.value)} />
          </Box>
        </Section>

        {/* ── Scope ── */}
        <Section T={T} label={t('mis.plScopeSection')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            mb: type === 'linked' ? 1.5 : 0 }}>
            <ToggleButtonGroup size="small" exclusive value={type} disabled={isEdit}
              onChange={(_, v) => v && setType(v)}>
              <ToggleButton value="free" sx={{ fontSize: '0.72rem', textTransform: 'none', px: 2 }}>
                {t('mis.plTypeFree')}
              </ToggleButton>
              <ToggleButton value="linked" sx={{ fontSize: '0.72rem', textTransform: 'none', px: 2 }}>
                {t('mis.plTypeLinked')}
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Draft vs final — the list shows this as a status rail, so it has
                to be settable here. */}
            <ToggleButtonGroup size="small" exclusive value={status}
              onChange={(_, v) => v && setStatus(v)}>
              {[
                { v: 'draft', l: t('mis.plStatusDraft'), c: '#ffb74d' },
                { v: 'final', l: t('mis.plStatusFinal'), c: '#81c784' },
              ].map((o) => (
                <ToggleButton key={o.v} value={o.v}
                  sx={{ fontSize: '0.72rem', textTransform: 'none', px: 2, gap: 0.6,
                    '&.Mui-selected': { color: o.c } }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%',
                    bgcolor: status === o.v ? o.c : T.TEXT_TER }} />
                  {o.l}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {type === 'linked' && (
            <>
              <Autocomplete
                multiple options={invoiceOptions} value={selectedInvoices}
                onChange={(_, v) => setSelectedInvoices(v)}
                onInputChange={(_, v) => setInvoiceSearch(v)}
                getOptionLabel={(o) => `#${o.docNumber}${o.customerSnapshot?.name ? ' — ' + o.customerSnapshot.name : ''}`}
                isOptionEqualToValue={(o, v) => String(o._id) === String(v._id)}
                renderInput={(params) => (
                  <TextField {...params} {...field} label={t('mis.plLinkedInvoicesLabel')} />
                )}
              />
              <Typography sx={{ fontSize: '0.66rem', color: T.TEXT_TER, mt: 0.75 }}>
                {scope.prefixes.size > 0
                  ? t('mis.plScopeActive', { codes: [...scope.prefixes].join(', ') })
                  : t('mis.plScopeHelper')}
              </Typography>
            </>
          )}
        </Section>

        {/* ── Stone: one or more names, picked from Inventory or typed ── */}
        <Section T={T} label={t('mis.plStoneSection')}>
          <StoneNamesField value={stoneNames} onChange={setStoneNames} T={T} touch={isXs}
            branchId={isEdit ? packingList.branchId : activeBranchId} />
        </Section>

        {/* ── Driver & vehicle ── */}
        <Section T={T} label={t('mis.plDriverSection')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: isXs ? '1fr' : '1fr 1fr', gap: 1.25 }}>
            <TextField {...field} label={t('mis.driverFullNameField')} value={driverInfo.fullName}
              onChange={(e) => setDriverInfo((d) => ({ ...d, fullName: e.target.value }))} />
            <TextField {...telField} label={t('mis.driverPhoneField')} value={driverInfo.phone}
              onChange={(e) => setDriverInfo((d) => ({ ...d, phone: e.target.value }))} />
            {/* National ID and smart number are one field: "123 / 456". A slash is typed in it, so the phone keypad that
                suits a pure number (inputMode numeric) would leave the person without one. */}
            <TextField {...idField} label={t('mis.driverNationalIdField')} value={driverInfo.nationalId}
              inputProps={{ dir: 'ltr', inputMode: 'text' }}
              onChange={(e) => setDriverInfo((d) => ({ ...d, nationalId: e.target.value }))} />
            <TextField {...field} label={t('mis.trailerPlateField')} value={vehicleInfo.trailerPlateNumber}
              inputProps={{ dir: 'ltr' }}
              onChange={(e) => setVehicleInfo((v) => ({ ...v, trailerPlateNumber: e.target.value }))} />
            <TextField {...idField} label={t('mis.trailerSmartNumberField')} value={vehicleInfo.trailerSmartNumber}
              onChange={(e) => setVehicleInfo((v) => ({ ...v, trailerSmartNumber: e.target.value }))} />
          </Box>
        </Section>

        {/* ── Route & handlers ── */}
        <Section T={T} label={t('mis.plLogisticsSection')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: isXs ? '1fr' : '1fr 1fr', gap: 1.25 }}>
            <TextField {...field} label={t('mis.loadingOfficerNameField')} value={loadingOfficer.name}
              onChange={(e) => setLoadingOfficer((o) => ({ ...o, name: e.target.value }))} />
            <TextField {...field} label={t('mis.customsAgentNameField')} value={customsAgent.name}
              onChange={(e) => setCustomsAgent((c) => ({ ...c, name: e.target.value }))} />

            {/* The origin and the final destination are picked from lists (added to from here), and walked with the
                keyboard: Enter from the field before opens the origin list, Enter on a place opens the destination list,
                Enter on that one moves on to the next field. See placeSelect.js. */}
            <PlaceSelect kind="origin" label={t('mis.originAddressField')} value={originAddress} onChange={setOriginAddress}
              rows={places.origin} canManage={canManagePlaces} onAdd={addPlace} onRemove={removePlace} onError={setError}
              touch={isXs} sx={{ gridColumn: isXs ? 'auto' : '1 / -1' }} />
            <PlaceSelect kind="destination" label={t('mis.destinationAddressField')} value={destinationAddress} onChange={setDestinationAddress}
              rows={places.destination} canManage={canManagePlaces} onAdd={addPlace} onRemove={removePlace} onError={setError}
              touch={isXs} sx={{ gridColumn: isXs ? 'auto' : '1 / -1' }} />

            <TextField {...field} label={t('mis.shippingDestinationField')} value={shippingDestination}
              helperText={t('mis.plShippingDestHelp')}
              onChange={(e) => setShippingDestination(e.target.value)} />
            <TextField {...field} type="number" label={t('mis.standardThicknessField')}
              value={standardThicknessCm} helperText={t('mis.plStandardThicknessHelp')}
              inputProps={{ inputMode: 'decimal', step: '0.1', min: 0 }}
              sx={{ '& input': { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
              onChange={(e) => applyStandardThickness(e.target.value)} />
          </Box>
        </Section>

        {/* ── Pallets ── */}
        <Section T={T} label={t('mis.plPalletsSection')}
          hint={t('mis.plTotalsSummary', { pcs: totals.pcs, sqm: totals.sqm.toFixed(4) })}>

          {outOfScope.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.72rem', py: 0.25 }}>
              {t('mis.plOutOfScopeWarning', { codes: outOfScope.join(', ') })}
            </Alert>
          )}
          {pallets.length > 1 && (
            <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC, mb: 0.5 }}>{t('mis.plPalletOrderHelp')}</Typography>
          )}
          <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC, mb: 1 }}>{t('mis.plCodeAutoHelp')}</Typography>
          <Box role="status" aria-live="polite"
            sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
            {announce}
          </Box>

          <Box ref={flip.containerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {pallets.map((pallet, pIdx) => (
              <PalletCard key={pallet._key || pIdx} pallet={pallet} index={pIdx}
                isLast={pIdx === pallets.length - 1} canRemove={pallets.length > 1}
                isMoved={Boolean(movedKey) && movedKey === pallet._key}
                clashIdText={clashes.id[pIdx] ? clashes.id[pIdx].join(', ') : ''}
                clashRefText={clashes.ref[pIdx] ? clashes.ref[pIdx].join(', ') : ''}
                takenText={takenFor(pallet.reference) ? takenLine(takenFor(pallet.reference)) : ''}
                codeHint={codeHints[pallet._key] || ''} canTemplate={canManagePlaces} onSaveTemplate={askTemplate}
                isXs={isXs} T={T} type={type} scopePrefixes={scope.prefixes} codeOptions={codeOptions}
                specOpen={specKey === pallet._key} canCreateVariety={canCreateVariety} searchProducts={searchForBuilder}
                onMoveBy={movePalletBy} onMoveTo={movePalletTo} onDuplicate={duplicatePallet} onRemove={removePallet}
                onField={setPalletField} onAddItem={addItem} onEnterRow={enterRow} onRemoveItem={removeItem} onItemField={setItemField}
                onResetSqm={resetSqm} onToggleSpec={toggleSpec} onUseBuilt={addBuiltItem} />
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: isXs ? 'column' : 'row', flexWrap: 'wrap', alignItems: isXs ? 'stretch' : 'center', gap: isXs ? 1 : 0.5, mt: 1.25 }}>
            <Button size="small" variant={isXs ? 'outlined' : 'text'} startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => addPallet()}
              sx={{ fontSize: '0.7rem', textTransform: 'none',
                ...(isXs ? { width: '100%', minHeight: 48, fontSize: '0.85rem', borderStyle: 'dashed' } : {}) }}>
              {t('mis.addPalletButton')}
            </Button>
            <PalletTemplateMenu templates={templates} canManage={canManagePlaces} onPick={addFromTemplate} onRemove={removeTemplate}
              onError={setError} touch={isXs} T={T} />
          </Box>
          <PalletCodeAdder forList branchId={isEdit ? packingList.branchId : activeBranchId} listId={isEdit ? packingList._id : null}
            heldCodes={pallets.map((p) => p.reference)} onAdd={addExisting} touch={isXs} T={T} />
          {/* or from a label form: its free pallets in one press, or the ones ticked; its origin and destination where the list has none */}
          <LabelFormPickerButton branchId={isEdit ? packingList.branchId : activeBranchId} listId={isEdit ? packingList._id : null}
            heldCodes={pallets.map((p) => p.reference)} hasOrigin={Boolean(originAddress.trim())} hasDestination={Boolean(destinationAddress.trim())}
            onAdd={(records, places) => {
              addExisting(records);
              if (places.originAddress) setOriginAddress(places.originAddress);
              if (places.destinationAddress) setDestinationAddress(places.destinationAddress);
            }} touch={isXs} T={T} />
          {!isXs && (
            <Typography sx={{ fontSize: '0.66rem', color: T.TEXT_TER, mt: 0.5 }}>{t('mis.plKeysHint')}</Typography>
          )}
        </Section>

        <TextField {...field} label={t('mis.sectionNotes')} value={notes} multiline minRows={2} fullWidth
          onChange={(e) => setNotes(e.target.value)} />

        {error && (
          <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.75rem', py: 0.25 }}>{error}</Alert>
        )}
      </Box>

      {/* Footer — running totals stay visible while saving. On a phone the totals take a line and the two buttons a second,
          full width and 48px tall, clear of the home-indicator bar. */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: { xs: 2, sm: 3 }, pt: 2,
        pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 2 }, borderTop: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip size="small" label={t('mis.plPalletCount', { count: pallets.filter((p) => String(p.palletId || '').trim()).length })}
            sx={footerChip} />
          <Chip size="small" label={`${totals.pcs} ${t('mis.plColPcs')}`} sx={footerChip} />
          <Chip size="small" label={`${totals.sqm.toFixed(4)} ${t('mis.plColSqm')}`} sx={footerChip} />
        </Box>
        <Box sx={{ marginInlineStart: { sm: 'auto' }, display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button onClick={handleClose} disabled={saving}
            sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none', minWidth: 90, flex: { xs: 2, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>
            {t('common.save')}
          </Button>
        </Box>
      </Box>
    </Drawer>

    <SavePalletTemplateDialog open={Boolean(templateFor)} pallet={templateFor ? pallets.find((p) => p._key === templateFor) : null} T={T}
      onSave={keepAsTemplate} onClose={() => setTemplateFor(null)} />

    {/* What to do with the unsent list on the way out. */}
    {draftable && (
      <DraftCloseDialog
        open={confirmClose}
        onCancel={() => setConfirmClose(false)}
        onKeep={async () => { await draftApi.flush(); setConfirmClose(false); onClose(); }}
        onDiscard={async () => { await draftApi.clear(); setConfirmClose(false); onClose(); }}
      />
    )}
    </>
  );
}
